import { ProfileAvatar } from '@components/UserMenu/UserMenu';
import { secondsToHMS } from '@lib/utils/time';
import { Tooltip } from '@mui/material';
import { BeginEndTimestamp } from '@stores/MomentMerge/MomentMergeStore';
import { videoPlayerStore, VideoPlayerStore } from '@stores/VideoPlayer';
import classNames from 'classnames';
import css from './Sentence.module.scss';
import { useSentenceCut } from './useSentenceCut';

type SentenceProps = {
  isActive: boolean;
  sentenceTimestamps: BeginEndTimestamp;
  momentTimestamps: BeginEndTimestamp | null;
  speaker: string;
  speakerColor: string;
  text: string;
  onClick: () => void;
  activeSentenceRef: React.RefObject<HTMLDivElement | null>;
  mode: 'moment' | 'full';
  store?: VideoPlayerStore;
};

export const Sentence = ({
  isActive,
  sentenceTimestamps,
  momentTimestamps,
  speaker,
  speakerColor,
  text,
  onClick,
  activeSentenceRef,
  mode,
  store = videoPlayerStore,
}: SentenceProps) => {
  const { cutLeftPercent, cutRightPercent, isCut } = useSentenceCut(
    sentenceTimestamps,
    momentTimestamps,
  );

  // Set CSS custom properties for the mask gradient
  const textStyle: React.CSSProperties | undefined =
    isCut && mode === 'moment'
      ? ({
          '--cut-left-percent': `${Math.min(100, cutLeftPercent).toFixed(2)}%`,
          '--cut-right-percent': `${Math.min(100, cutRightPercent).toFixed(2)}%`,
        } as React.CSSProperties)
      : undefined;

  return (
    <div
      key={`${sentenceTimestamps.startTime}-${sentenceTimestamps.endTime}`}
      ref={isActive ? activeSentenceRef : null}
      className={classNames(css.sentence, {
        [css.active]: isActive,
      })}
      onClick={onClick}
    >
      <Tooltip
        title={speaker}
        arrow
        placement="top"
        slotProps={{ popper: { disablePortal: true } }}
      >
        <div>
          <ProfileAvatar size="small" name={speaker} bgColor={speakerColor} />
        </div>
      </Tooltip>
      <div className={css.timing}>
        {secondsToHMS(
          store.mode === 'moment' && momentTimestamps
            ? Math.max(0, sentenceTimestamps.startTime - momentTimestamps.startTime)
            : sentenceTimestamps.startTime,
        )}
      </div>
      <div
        className={classNames(css.text, {
          [css.cut]: isCut,
        })}
        style={textStyle}
      >
        <span className={isCut ? css.cutText : undefined}>{text}</span>
      </div>
    </div>
  );
};
